import { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { getReceptorName } from 'src/Utils/Receptors/getReceptorName'

/**
 * How a document receptor connection (one receptor row) is named to users.
 *
 * An account can hold several connections of one model, say two Google Drive
 * service accounts. A row created from the Receptors page carries a `name`;
 * one created from Account Settings carries only its nickname (`tenantid`),
 * and older rows carry the placeholder nickname the activate form filled in
 * when the field was hidden. The label is the first of: name, a real
 * nickname, the service-account identity, the model's name. The same rule
 * lives in ntrced (`documentsource` DisplayName) so both sides agree.
 */

/** The nickname the activate form fills in when the field is hidden. */
export const placeholderTenantId = (modelId: string): string =>
  `${modelId}-tenant`

export const isPlaceholderTenant = (receptor: ReceptorRecord): boolean => {
  const tenant = (receptor.getTenantid() || '').trim()
  return tenant === '' || tenant === placeholderTenantId(receptor.getModelid())
}

/**
 * The identity the connection acts as, from its stored credentials: the
 * service-account email for Google Drive, nothing for other models yet.
 * Pass `identity` from DescribeSource instead when the caller has it.
 */
export const connectionIdentity = (receptor: ReceptorRecord): string => {
  const raw = receptor.getCredentials()
  if (!raw) return ''
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const email = parsed.service_account_email
    return typeof email === 'string' ? email.trim() : ''
  } catch {
    return ''
  }
}

export const documentSourceLabel = (
  receptor: ReceptorRecord,
  identity?: string,
): string => {
  const name = (receptor.getName() || '').trim()
  if (name) return name
  if (!isPlaceholderTenant(receptor)) return receptor.getTenantid().trim()
  const who = (identity || connectionIdentity(receptor)).trim()
  if (who) return who
  return getReceptorName(receptor.getModelid())
}

/** The identity, when the label does not already show it. */
export const documentSourceSecondary = (
  receptor: ReceptorRecord,
  identity?: string,
): string => {
  const who = (identity || connectionIdentity(receptor)).trim()
  return who && who !== documentSourceLabel(receptor, identity) ? who : ''
}

/** Vendor name plus the label, e.g. "Google Drive (Finance)". */
export const documentSourceQualifiedLabel = (
  receptor: ReceptorRecord,
  identity?: string,
): string => {
  const vendor = getReceptorName(receptor.getModelid())
  const label = documentSourceLabel(receptor, identity)
  return label === vendor ? vendor : `${vendor} (${label})`
}

/**
 * True when the account has more than one enabled connection of the model,
 * which is when the UI starts telling connections apart. With one, every
 * screen stays as it was.
 */
export const hasSeveralConnections = (
  receptors: ReceptorRecord[] | undefined,
  modelId: string,
): boolean =>
  (receptors || []).filter(
    (r) => r.getModelid() === modelId && r.getIsenabled(),
  ).length > 1
